import { useState, useEffect } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, MoveUp, MoveDown, Loader2, ChevronDown, ChevronUp, GripVertical, Eye, EyeOff } from "lucide-react";

const SECTION_LABELS = {
  hero: "Hero Section",
  emotional_intro: "Emotional Intro",
  who_this_is_for: "Who This Is For",
  what_this_is: "What This Is",
  clearing_mirror: "The Clearing Mirror",
  how_it_works: "How It Works",
  dialogue: "Conversation Example",
  why_mickey: "Why Mickey?",
  what_this_is_not: "What This Is Not (Trust)",
  packages: "Packages / Pricing",
  reflection_library: "Reflection Library CTA",
  scholarship: "Scholarship Section",
  testimonials: "Reflections From The Chair",
  faq: "FAQ",
  final_cta: "Final CTA",
  media_kit: "Media Kit",
  workshops_cta: "Workshops CTA",
  footer_note: "Footer & Contact",
};

export default function AdminMirrorConversations() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openSections, setOpenSections] = useState({});

  const load = async () => {
    try {
      const { data } = await http.get("/mirror/admin/settings");
      setSettings(data);
    } catch { toast.error("Failed to load Mirror data"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (updates) => {
    setSaving(true);
    try {
      const { data } = await http.patch("/mirror/admin/settings", updates);
      setSettings(data);
      toast.success("Saved");
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const toggle = (k) => setOpenSections(p => ({ ...p, [k]: !p[k] }));

  if (!settings) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]" /></div>;

  const order = settings.section_order || Object.keys(SECTION_LABELS);
  const hidden = settings.hidden_sections || [];

  const moveSection = (idx, dir) => {
    if (idx + dir < 0 || idx + dir >= order.length) return;
    const o = [...order]; [o[idx], o[idx + dir]] = [o[idx + dir], o[idx]];
    setSettings({ ...settings, section_order: o });
    save({ section_order: o });
  };

  const toggleVisibility = (key) => {
    const h = hidden.includes(key) ? hidden.filter(k => k !== key) : [...hidden, key];
    setSettings({ ...settings, hidden_sections: h });
    save({ hidden_sections: h });
  };

  const isHidden = (key) => hidden.includes(key);

  return (
    <div className="space-y-6" data-testid="admin-mirror">
      <div>
        <h2 className="font-display text-3xl text-[#F4F1EA]">Mirror Conversations&trade;</h2>
        <p className="font-body text-[13px] text-[#8a857d] mt-1">Edit every section. Use arrows to reorder sections on the public page.</p>
      </div>

      {/* SEO */}
      <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3">
        <h3 className="font-ui text-[12px] tracking-[0.2em] text-[#C9A055] uppercase border-b border-white/10 pb-2">SEO Settings</h3>
        <Field label="SEO Title" value={settings.seo_title || ""} onChange={v => setSettings({ ...settings, seo_title: v })} onBlur={() => save({ seo_title: settings.seo_title })} />
        <Field label="Meta Description" value={settings.seo_description || ""} onChange={v => setSettings({ ...settings, seo_description: v })} onBlur={() => save({ seo_description: settings.seo_description })} textarea rows={2} />
      </div>

      {/* Sections */}
      <div className="space-y-2">
        {order.map((key, idx) => (
          <div key={key} className={`bg-[#0a0a0a] border overflow-hidden ${isHidden(key) ? "border-white/5 opacity-50" : "border-white/10"}`} data-testid={`admin-section-${key}`}>
            <div className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-white/[0.02]" onClick={() => toggle(key)}>
              <div className="flex flex-col gap-0.5 shrink-0" onClick={e => e.stopPropagation()}>
                <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="p-0.5 text-[#8a857d] hover:text-[#C9A055] disabled:opacity-20" data-testid={`move-up-${key}`}><MoveUp size={14} /></button>
                <button onClick={() => moveSection(idx, 1)} disabled={idx === order.length - 1} className="p-0.5 text-[#8a857d] hover:text-[#C9A055] disabled:opacity-20" data-testid={`move-down-${key}`}><MoveDown size={14} /></button>
              </div>
              <GripVertical size={14} className="text-[#5a5650] shrink-0" />
              <button onClick={e => { e.stopPropagation(); toggleVisibility(key); }}
                className={`shrink-0 p-1 rounded transition-colors ${isHidden(key) ? "text-red-400/70 hover:text-red-400" : "text-emerald-500/70 hover:text-emerald-500"}`}
                title={isHidden(key) ? "Show on public page" : "Hide from public page"}
                data-testid={`toggle-visibility-${key}`}>
                {isHidden(key) ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
              <span className={`font-ui text-[12px] tracking-[0.2em] uppercase flex-1 ${isHidden(key) ? "text-[#8a857d] line-through" : "text-[#C9A055]"}`}>{SECTION_LABELS[key] || key}</span>
              {isHidden(key) && <span className="text-[9px] tracking-wider uppercase text-red-400/60 mr-2">Hidden</span>}
              <span className="text-[10px] text-[#5a5650] font-mono mr-2">#{idx + 1}</span>
              {openSections[key] ? <ChevronUp size={16} className="text-[#8a857d]" /> : <ChevronDown size={16} className="text-[#8a857d]" />}
            </div>
            {openSections[key] && (
              <div className="border-t border-white/5 p-5">
                <SectionEditor k={key} s={settings} set={setSettings} save={save} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionEditor({ k, s, set, save }) {
  switch (k) {
    case "hero": return <HeroEd s={s} set={set} save={save} />;
    case "emotional_intro": return <TextBlockEd field="emotional_intro" label="Emotional Intro Text" s={s} set={set} save={save} />;
    case "who_this_is_for": return <WhoEd s={s} set={set} save={save} />;
    case "what_this_is": return <WhatEd s={s} set={set} save={save} />;
    case "clearing_mirror": return <ClearingMirrorEd s={s} set={set} save={save} />;
    case "how_it_works": return <HowEd s={s} set={set} save={save} />;
    case "dialogue": return <DialogueEd s={s} set={set} save={save} />;
    case "why_mickey": return <WhyMickeyEd s={s} set={set} save={save} />;
    case "what_this_is_not": return <NotEd s={s} set={set} save={save} />;
    case "packages": return <PackagesEd s={s} set={set} save={save} />;
    case "reflection_library": return <ReflectionLibraryEd s={s} set={set} save={save} />;
    case "scholarship": return <ScholarshipEd s={s} set={set} save={save} />;
    case "testimonials": return <TestimonialsEd s={s} set={set} save={save} />;
    case "faq": return <FaqEd s={s} set={set} save={save} />;
    case "final_cta": return <FinalEd s={s} set={set} save={save} />;
    case "media_kit": return <MediaKitEd s={s} set={set} save={save} />;
    case "footer_note": return <FooterEd s={s} set={set} save={save} />;
    default: return <p className="text-[#8a857d] text-sm">No editor for this section.</p>;
  }
}

/* ═══ HERO ═══ */
function HeroEd({ s, set, save }) {
  return (
    <div className="space-y-4">
      <Field label="Headline" value={s.hero_headline || ""} onChange={v => set({ ...s, hero_headline: v })} textarea rows={3} />
      <Field label="Subheadline" value={s.hero_subheadline || ""} onChange={v => set({ ...s, hero_subheadline: v })} textarea rows={3} />
      <Field label="Trust Line" value={s.hero_trust_line || ""} onChange={v => set({ ...s, hero_trust_line: v })} />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Primary Button Text" value={s.hero_primary_btn || ""} onChange={v => set({ ...s, hero_primary_btn: v })} />
        <Field label="Secondary Button Text" value={s.hero_secondary_btn || ""} onChange={v => set({ ...s, hero_secondary_btn: v })} />
      </div>
      <Uploader label="Hero Background Image" url={s.hero_image} onUpload={url => { set({ ...s, hero_image: url }); save({ hero_image: url }); }} />
      <SaveBtn onClick={() => save({ hero_headline: s.hero_headline, hero_subheadline: s.hero_subheadline, hero_trust_line: s.hero_trust_line, hero_primary_btn: s.hero_primary_btn, hero_secondary_btn: s.hero_secondary_btn })} />
    </div>
  );
}

/* ═══ TEXT BLOCK (reusable) ═══ */
function TextBlockEd({ field, label, s, set, save }) {
  return (
    <div className="space-y-4">
      <Field label={label} value={s[field] || ""} onChange={v => set({ ...s, [field]: v })} textarea rows={10} />
      <SaveBtn onClick={() => save({ [field]: s[field] })} />
    </div>
  );
}

/* ═══ WHO ═══ */
function WhoEd({ s, set, save }) {
  return (
    <div className="space-y-4">
      <Field label="Heading" value={s.who_heading || ""} onChange={v => set({ ...s, who_heading: v })} />
      <ArrayEditor items={s.who_cards} onSave={v => save({ who_cards: v })} renderItem={(it, i, upd) => (
        <Field label={`Card ${i+1}`} value={it.text} onChange={v => upd({ ...it, text: v })} />
      )} newItem={() => ({ text: "", icon: "" })} />
      <Field label="Subtext" value={s.who_subtext || ""} onChange={v => set({ ...s, who_subtext: v })} textarea rows={2} />
      <Field label="Button Text" value={s.who_btn_text || ""} onChange={v => set({ ...s, who_btn_text: v })} />
      <SaveBtn onClick={() => save({ who_heading: s.who_heading, who_subtext: s.who_subtext, who_btn_text: s.who_btn_text })} />
    </div>
  );
}

/* ═══ WHAT ═══ */
function WhatEd({ s, set, save }) {
  return (
    <div className="space-y-4">
      <Field label="Heading" value={s.what_heading || ""} onChange={v => set({ ...s, what_heading: v })} />
      <Field label="Copy" value={s.what_copy || ""} onChange={v => set({ ...s, what_copy: v })} textarea rows={8} />
      <Field label="Pull Quote" value={s.what_pullquote || ""} onChange={v => set({ ...s, what_pullquote: v })} />
      <Uploader label="Section Image" url={s.what_image} onUpload={url => { set({ ...s, what_image: url }); save({ what_image: url }); }} />
      <SaveBtn onClick={() => save({ what_heading: s.what_heading, what_copy: s.what_copy, what_pullquote: s.what_pullquote })} />
    </div>
  );
}

/* ═══ HOW ═══ */
function HowEd({ s, set, save }) {
  return (
    <div className="space-y-4">
      <Field label="Heading" value={s.how_heading || ""} onChange={v => set({ ...s, how_heading: v })} onBlur={() => save({ how_heading: s.how_heading })} />
      <ArrayEditor items={s.how_steps} onSave={v => save({ how_steps: v })} renderItem={(it, i, upd) => (
        <div className="space-y-2 w-full">
          <input placeholder="Title" value={it.title} onChange={e => upd({ ...it, title: e.target.value })} className="stc-input w-full text-sm font-bold" />
          <textarea placeholder="Description" value={it.desc} onChange={e => upd({ ...it, desc: e.target.value })} className="stc-input w-full text-sm" rows={2} />
        </div>
      )} newItem={() => ({ title: "", desc: "" })} />
    </div>
  );
}

/* ═══ DIALOGUE ═══ */
function DialogueEd({ s, set, save }) {
  return (
    <div className="space-y-4">
      <Field label="Heading" value={s.dialogue_heading || ""} onChange={v => set({ ...s, dialogue_heading: v })} />
      <ArrayEditor items={s.dialogue_lines} onSave={v => save({ dialogue_lines: v })} renderItem={(it, i, upd) => (
        <div className="flex gap-2 w-full items-start">
          <select value={it.speaker} onChange={e => upd({ ...it, speaker: e.target.value })} className="stc-input w-28 text-sm">
            <option value="person">Person</option>
            <option value="mickey">Mickey</option>
          </select>
          <input placeholder="Line text" value={it.text} onChange={e => upd({ ...it, text: e.target.value })} className="stc-input flex-1 text-sm" />
        </div>
      )} newItem={() => ({ speaker: "person", text: "" })} />
      <Field label="Closing Text" value={s.dialogue_closing || ""} onChange={v => set({ ...s, dialogue_closing: v })} textarea rows={3} />
      <SaveBtn onClick={() => save({ dialogue_heading: s.dialogue_heading, dialogue_closing: s.dialogue_closing })} />
    </div>
  );
}

/* ═══ WHY MICKEY ═══ */
function WhyMickeyEd({ s, set, save }) {
  const [creds, setCreds] = useState(s.why_mickey_credentials || []);
  useEffect(() => setCreds(s.why_mickey_credentials || []), [s.why_mickey_credentials]);

  return (
    <div className="space-y-4">
      <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase">Opening Question</h4>
      <Field label="Opening Question" value={s.why_mickey_opening_q || ""} onChange={v => set({ ...s, why_mickey_opening_q: v })} />
      <Field label="Opening Answer" value={s.why_mickey_opening_a || ""} onChange={v => set({ ...s, why_mickey_opening_a: v })} textarea rows={5} />
      <SaveBtn onClick={() => save({ why_mickey_opening_q: s.why_mickey_opening_q, why_mickey_opening_a: s.why_mickey_opening_a })} />

      <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase pt-4">Main Copy</h4>
      <Field label="Main Copy" value={s.why_mickey_main_copy || ""} onChange={v => set({ ...s, why_mickey_main_copy: v })} textarea rows={12} />
      <Uploader label="Photo" url={s.why_mickey_image} onUpload={url => { set({ ...s, why_mickey_image: url }); save({ why_mickey_image: url }); }} />
      <SaveBtn onClick={() => save({ why_mickey_main_copy: s.why_mickey_main_copy })} />

      <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase pt-4">Built Through Real Life</h4>
      <Field label="Heading" value={s.why_mickey_built_heading || ""} onChange={v => set({ ...s, why_mickey_built_heading: v })} />
      <Field label="Copy" value={s.why_mickey_built_copy || ""} onChange={v => set({ ...s, why_mickey_built_copy: v })} textarea rows={8} />
      <SaveBtn onClick={() => save({ why_mickey_built_heading: s.why_mickey_built_heading, why_mickey_built_copy: s.why_mickey_built_copy })} />

      <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase pt-4">My Background & Credentials</h4>
      <Field label="Background Heading" value={s.why_mickey_bg_heading || ""} onChange={v => set({ ...s, why_mickey_bg_heading: v })} />
      <div className="space-y-2">
        {creds.map((c, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input value={c} onChange={e => { const n = [...creds]; n[i] = e.target.value; setCreds(n); }} className="stc-input flex-1 text-sm" />
            <button onClick={() => setCreds(creds.filter((_, j) => j !== i))} className="btn-outline px-1.5 border-red-900/50 text-red-500"><Trash2 size={12} /></button>
          </div>
        ))}
        <button onClick={() => setCreds([...creds, ""])} className="btn-outline text-xs w-full justify-center"><Plus size={14} /> Add Credential</button>
        <SaveBtn onClick={() => save({ why_mickey_credentials: creds })} label="Save Credentials" />
      </div>
      <Field label="Background Copy" value={s.why_mickey_bg_copy || ""} onChange={v => set({ ...s, why_mickey_bg_copy: v })} textarea rows={5} />
      <SaveBtn onClick={() => save({ why_mickey_bg_heading: s.why_mickey_bg_heading, why_mickey_bg_copy: s.why_mickey_bg_copy })} />

      <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase pt-4">Why MC Exists</h4>
      <Field label="Heading" value={s.why_mickey_exists_heading || ""} onChange={v => set({ ...s, why_mickey_exists_heading: v })} />
      <Field label="Copy" value={s.why_mickey_exists_copy || ""} onChange={v => set({ ...s, why_mickey_exists_copy: v })} textarea rows={6} />
      <SaveBtn onClick={() => save({ why_mickey_exists_heading: s.why_mickey_exists_heading, why_mickey_exists_copy: s.why_mickey_exists_copy })} />

      <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase pt-4">My Promise</h4>
      <Field label="Heading" value={s.why_mickey_promise_heading || ""} onChange={v => set({ ...s, why_mickey_promise_heading: v })} />
      <Field label="Copy" value={s.why_mickey_promise_copy || ""} onChange={v => set({ ...s, why_mickey_promise_copy: v })} textarea rows={5} />
      <Field label="Button Text" value={s.why_mickey_btn_text || ""} onChange={v => set({ ...s, why_mickey_btn_text: v })} />
      <Field label="Button Target" value={s.why_mickey_btn_target || ""} onChange={v => set({ ...s, why_mickey_btn_target: v })} />
      <Field label="Subtext under button" value={s.why_mickey_btn_subtext || ""} onChange={v => set({ ...s, why_mickey_btn_subtext: v })} />
      <SaveBtn onClick={() => save({ why_mickey_promise_heading: s.why_mickey_promise_heading, why_mickey_promise_copy: s.why_mickey_promise_copy, why_mickey_btn_text: s.why_mickey_btn_text, why_mickey_btn_target: s.why_mickey_btn_target, why_mickey_btn_subtext: s.why_mickey_btn_subtext })} />
    </div>
  );
}

/* ═══ NOT ═══ */
function NotEd({ s, set, save }) {
  const [items, setItems] = useState(s.not_items || []);
  useEffect(() => setItems(s.not_items || []), [s.not_items]);
  return (
    <div className="space-y-4">
      <Field label="Heading" value={s.not_heading || ""} onChange={v => set({ ...s, not_heading: v })} />
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input value={it} onChange={e => { const n = [...items]; n[i] = e.target.value; setItems(n); }} className="stc-input flex-1 text-sm" />
            <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="btn-outline px-1.5 border-red-900/50 text-red-500"><Trash2 size={12} /></button>
          </div>
        ))}
        <button onClick={() => setItems([...items, ""])} className="btn-outline text-xs w-full justify-center"><Plus size={14} /> Add Item</button>
        <SaveBtn onClick={() => save({ not_items: items })} label="Save Items" />
      </div>
      <Field label="Closing Text" value={s.not_closing || ""} onChange={v => set({ ...s, not_closing: v })} textarea rows={3} />
      <Field label="Emergency Notice" value={s.not_emergency || ""} onChange={v => set({ ...s, not_emergency: v })} textarea rows={2} />
      <SaveBtn onClick={() => save({ not_heading: s.not_heading, not_closing: s.not_closing, not_emergency: s.not_emergency })} />
    </div>
  );
}

/* ═══ PACKAGES ═══ */
function PackagesEd({ s, set, save }) {
  return (
    <div className="space-y-4">
      <Field label="Heading" value={s.packages_heading || ""} onChange={v => set({ ...s, packages_heading: v })} onBlur={() => save({ packages_heading: s.packages_heading })} />

      {/* Special / Sale Pricing Toggle */}
      <div className="bg-[#111] border border-white/10 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase">Special / Sale Pricing</h4>
            <p className="text-[10px] text-[#8a857d] mt-0.5">When ON, packages with a sale_price show the original price crossed out with the sale price.</p>
          </div>
          <Toggle checked={s.special_enabled || false} onChange={v => { set({ ...s, special_enabled: v }); save({ special_enabled: v }); }} />
        </div>
        {s.special_enabled && (
          <div className="pt-2 border-t border-white/5">
            <Field label="Sale Label (e.g. 'Limited Time', 'Launch Special')" value={s.special_label || ""} onChange={v => set({ ...s, special_label: v })} onBlur={() => save({ special_label: s.special_label })} />
          </div>
        )}
      </div>

      {/* PWYW Toggle */}
      <div className="bg-[#111] border border-white/10 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase">Pay What You Want</h4>
            <p className="text-[10px] text-[#8a857d] mt-0.5">When ON, users choose their own amount. When OFF, fixed pricing only.</p>
          </div>
          <Toggle checked={s.pwyw_enabled || false} onChange={v => { set({ ...s, pwyw_enabled: v }); save({ pwyw_enabled: v }); }} />
        </div>
        {s.pwyw_enabled && (
          <div className="space-y-2 pt-2 border-t border-white/5">
            <Field label="PWYW Display Text" value={s.pwyw_text || ""} onChange={v => set({ ...s, pwyw_text: v })} onBlur={() => save({ pwyw_text: s.pwyw_text })} />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Minimum Price</label>
                <div className="flex items-center gap-2"><span className="text-[#8a857d]">$</span>
                  <input type="number" value={s.min_price || 30} onChange={e => set({ ...s, min_price: parseFloat(e.target.value) })} onBlur={() => save({ min_price: s.min_price })} className="stc-input w-full" />
                </div>
              </div>
              <div>
                <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Suggested Price</label>
                <div className="flex items-center gap-2"><span className="text-[#8a857d]">$</span>
                  <input type="number" value={s.suggested_price || 0} onChange={e => set({ ...s, suggested_price: parseFloat(e.target.value) })} onBlur={() => save({ suggested_price: s.suggested_price })} className="stc-input w-full" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ArrayEditor items={s.packages} onSave={v => save({ packages: v })} renderItem={(it, i, upd) => (
        <div className="space-y-2 w-full">
          <input placeholder="Title" value={it.title} onChange={e => upd({ ...it, title: e.target.value })} className="stc-input w-full text-sm font-bold" />
          <div className="grid grid-cols-3 gap-2">
            <input placeholder="Subtitle" value={it.subtitle} onChange={e => upd({ ...it, subtitle: e.target.value })} className="stc-input text-sm" />
            <input type="number" placeholder="Price" value={it.price} onChange={e => upd({ ...it, price: parseFloat(e.target.value) || 0 })} className="stc-input text-sm" />
            <input type="number" placeholder="Sale Price (0=none)" value={it.sale_price || 0} onChange={e => upd({ ...it, sale_price: parseFloat(e.target.value) || 0 })} className="stc-input text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Button Text" value={it.btn_text} onChange={e => upd({ ...it, btn_text: e.target.value })} className="stc-input text-sm" />
            <select value={it.type} onChange={e => upd({ ...it, type: e.target.value })} className="stc-input text-sm">
              <option value="stripe">Stripe Checkout</option>
              <option value="contact">Contact Form</option>
            </select>
          </div>
          <textarea placeholder="Description" value={it.desc} onChange={e => upd({ ...it, desc: e.target.value })} className="stc-input w-full text-sm" rows={2} />
          <label className="flex items-center gap-2 text-xs text-[#8a857d]">
            <input type="checkbox" checked={it.popular || false} onChange={e => upd({ ...it, popular: e.target.checked })} className="accent-[#C9A055]" /> Most Popular
          </label>
        </div>
      )} newItem={() => ({ id: Math.random().toString(36).slice(2, 9), title: "", subtitle: "", desc: "", price: 0, sale_price: 0, btn_text: "Take a Seat", type: "stripe", popular: false })} />
    </div>
  );
}

/* ═══ CLEARING MIRROR ═══ */
function ClearingMirrorEd({ s, set, save }) {
  return (
    <div className="space-y-4">
      <div className="bg-[#111] border border-white/10 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase">Section Enabled</h4>
            <p className="text-[10px] text-[#8a857d] mt-0.5">Show or hide the Clearing Mirror section.</p>
          </div>
          <Toggle checked={s.clearing_mirror_enabled !== false} onChange={v => { set({ ...s, clearing_mirror_enabled: v }); save({ clearing_mirror_enabled: v }); }} />
        </div>
      </div>
      <Field label="Heading" value={s.clearing_mirror_heading || ""} onChange={v => set({ ...s, clearing_mirror_heading: v })} />
      <Field label="Subtext (scroll prompt)" value={s.clearing_mirror_subtext || ""} onChange={v => set({ ...s, clearing_mirror_subtext: v })} />
      <Field label="Text Line 1 (appears at ~40% scroll)" value={s.clearing_mirror_text1 || ""} onChange={v => set({ ...s, clearing_mirror_text1: v })} />
      <Field label="Text Line 2 (appears at ~65% scroll)" value={s.clearing_mirror_text2 || ""} onChange={v => set({ ...s, clearing_mirror_text2: v })} />
      <Field label="Text Line 3 — Gold (appears at ~90% scroll)" value={s.clearing_mirror_text3 || ""} onChange={v => set({ ...s, clearing_mirror_text3: v })} />
      <Field label="Button Text" value={s.clearing_mirror_btn_text || ""} onChange={v => set({ ...s, clearing_mirror_btn_text: v })} />
      <Field label="Button Destination (e.g. #packages)" value={s.clearing_mirror_btn_target || ""} onChange={v => set({ ...s, clearing_mirror_btn_target: v })} />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Background Color" value={s.clearing_mirror_bg || "#050505"} onChange={v => set({ ...s, clearing_mirror_bg: v })} />
        <Field label="Gold Accent Color" value={s.clearing_mirror_accent || "#D4AF37"} onChange={v => set({ ...s, clearing_mirror_accent: v })} />
      </div>
      <Uploader label="Mirror Image" url={s.clearing_mirror_image} onUpload={url => { set({ ...s, clearing_mirror_image: url }); save({ clearing_mirror_image: url }); }} />
      <SaveBtn onClick={() => save({ clearing_mirror_heading: s.clearing_mirror_heading, clearing_mirror_subtext: s.clearing_mirror_subtext, clearing_mirror_text1: s.clearing_mirror_text1, clearing_mirror_text2: s.clearing_mirror_text2, clearing_mirror_text3: s.clearing_mirror_text3, clearing_mirror_btn_text: s.clearing_mirror_btn_text, clearing_mirror_btn_target: s.clearing_mirror_btn_target, clearing_mirror_bg: s.clearing_mirror_bg, clearing_mirror_accent: s.clearing_mirror_accent })} />
    </div>
  );
}

/* ═══ REFLECTION LIBRARY SECTION ═══ */
function ReflectionLibraryEd({ s, set, save }) {
  const [features, setFeatures] = useState(s.rl_features || []);
  const [previews, setPreviews] = useState(s.rl_previews || []);
  useEffect(() => { setFeatures(s.rl_features || []); setPreviews(s.rl_previews || []); }, [s.rl_features, s.rl_previews]);

  return (
    <div className="space-y-4">
      <div className="bg-[#111] border border-white/10 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase">Section Enabled</h4>
            <p className="text-[10px] text-[#8a857d] mt-0.5">Show or hide the Reflection Library section.</p>
          </div>
          <Toggle checked={s.rl_enabled !== false} onChange={v => { set({ ...s, rl_enabled: v }); save({ rl_enabled: v }); }} />
        </div>
      </div>

      <Field label="Headline" value={s.rl_headline || ""} onChange={v => set({ ...s, rl_headline: v })} />
      <Field label="Subheadline" value={s.rl_subheadline || ""} onChange={v => set({ ...s, rl_subheadline: v })} textarea rows={3} />
      <Field label="Body Copy" value={s.rl_body || ""} onChange={v => set({ ...s, rl_body: v })} textarea rows={6} />
      <SaveBtn onClick={() => save({ rl_headline: s.rl_headline, rl_subheadline: s.rl_subheadline, rl_body: s.rl_body })} />

      <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase pt-2">Feature Cards</h4>
      <ArrayEditor items={features} onSave={v => save({ rl_features: v })} renderItem={(it, i, upd) => (
        <div className="space-y-2 w-full">
          <input placeholder="Title" value={it.title} onChange={e => upd({ ...it, title: e.target.value })} className="stc-input w-full text-sm font-bold" />
          <input placeholder="Description" value={it.desc} onChange={e => upd({ ...it, desc: e.target.value })} className="stc-input w-full text-sm" />
        </div>
      )} newItem={() => ({ title: "", desc: "" })} />

      <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase pt-2">Featured Worksheet Titles</h4>
      <div className="space-y-2">
        {previews.map((p, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input value={p} onChange={e => { const n = [...previews]; n[i] = e.target.value; setPreviews(n); }} className="stc-input flex-1 text-sm" />
            <button onClick={() => setPreviews(previews.filter((_, j) => j !== i))} className="btn-outline px-1.5 border-red-900/50 text-red-500"><Trash2 size={12} /></button>
          </div>
        ))}
        <button onClick={() => setPreviews([...previews, ""])} className="btn-outline text-xs w-full justify-center"><Plus size={14} /> Add Title</button>
        <SaveBtn onClick={() => save({ rl_previews: previews })} label="Save Titles" />
      </div>

      <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase pt-2">CTA Button</h4>
      <Field label="Button Text" value={s.rl_btn_text || ""} onChange={v => set({ ...s, rl_btn_text: v })} />
      <Field label="Button Link" value={s.rl_btn_link || ""} onChange={v => set({ ...s, rl_btn_link: v })} />
      <Field label="Secondary Text (under button)" value={s.rl_secondary_text || ""} onChange={v => set({ ...s, rl_secondary_text: v })} textarea rows={3} />
      <SaveBtn onClick={() => save({ rl_btn_text: s.rl_btn_text, rl_btn_link: s.rl_btn_link, rl_secondary_text: s.rl_secondary_text })} />
    </div>
  );
}

/* ═══ SCHOLARSHIP ═══ */
function ScholarshipEd({ s, set, save }) {
  return (
    <div className="space-y-4">
      {/* Toggles */}
      <div className="bg-[#111] border border-white/10 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase">Scholarship Section</h4>
            <p className="text-[10px] text-[#8a857d] mt-0.5">Show or hide the scholarship section on the public page.</p>
          </div>
          <Toggle checked={s.scholarship_enabled || false} onChange={v => { set({ ...s, scholarship_enabled: v }); save({ scholarship_enabled: v }); }} />
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-white/5">
          <div>
            <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase">Scholarship Form</h4>
            <p className="text-[10px] text-[#8a857d] mt-0.5">Enable/disable the scholarship request form.</p>
          </div>
          <Toggle checked={s.scholarship_form_enabled || false} onChange={v => { set({ ...s, scholarship_form_enabled: v }); save({ scholarship_form_enabled: v }); }} />
        </div>
      </div>

      <Field label="Heading" value={s.scholarship_heading || ""} onChange={v => set({ ...s, scholarship_heading: v })} />
      <Field label="Copy" value={s.scholarship_copy || ""} onChange={v => set({ ...s, scholarship_copy: v })} textarea rows={6} />
      <Field label="Button Text" value={s.scholarship_btn_text || ""} onChange={v => set({ ...s, scholarship_btn_text: v })} />
      <Field label="Availability Message" value={s.scholarship_availability || ""} onChange={v => set({ ...s, scholarship_availability: v })} />
      <Field label="Confirmation Message (after submit)" value={s.scholarship_confirmation || ""} onChange={v => set({ ...s, scholarship_confirmation: v })} textarea rows={3} />
      <Uploader label="Section Image (optional)" url={s.scholarship_image} onUpload={url => { set({ ...s, scholarship_image: url }); save({ scholarship_image: url }); }} />
      <SaveBtn onClick={() => save({ scholarship_heading: s.scholarship_heading, scholarship_copy: s.scholarship_copy, scholarship_btn_text: s.scholarship_btn_text, scholarship_availability: s.scholarship_availability, scholarship_confirmation: s.scholarship_confirmation })} />
    </div>
  );
}

/* ═══ TESTIMONIALS ═══ */
function TestimonialsEd({ s, set, save }) {
  return (
    <div className="space-y-4">
      <Field label="Section Heading" value={s.testimonials_heading || ""} onChange={v => set({ ...s, testimonials_heading: v })} onBlur={() => save({ testimonials_heading: s.testimonials_heading })} />
      <ArrayEditor items={s.testimonials} onSave={v => save({ testimonials: v })} renderItem={(it, i, upd) => (
        <div className="space-y-2 w-full">
          <textarea placeholder="Quote" value={it.quote} onChange={e => upd({ ...it, quote: e.target.value })} className="stc-input w-full text-sm" rows={3} />
          <input placeholder="Name (optional)" value={it.name || ""} onChange={e => upd({ ...it, name: e.target.value })} className="stc-input w-full text-sm" />
        </div>
      )} newItem={() => ({ quote: "", name: "" })} />
    </div>
  );
}

/* ═══ FAQ ═══ */
function FaqEd({ s, set, save }) {
  return (
    <div className="space-y-4">
      <Field label="Section Heading" value={s.faq_heading || ""} onChange={v => set({ ...s, faq_heading: v })} onBlur={() => save({ faq_heading: s.faq_heading })} />
      <ArrayEditor items={s.faq} onSave={v => save({ faq: v })} renderItem={(it, i, upd) => (
        <div className="space-y-2 w-full">
          <input placeholder="Question" value={it.q} onChange={e => upd({ ...it, q: e.target.value })} className="stc-input w-full text-sm font-bold" />
          <textarea placeholder="Answer" value={it.a} onChange={e => upd({ ...it, a: e.target.value })} className="stc-input w-full text-sm" rows={3} />
        </div>
      )} newItem={() => ({ q: "", a: "" })} />
    </div>
  );
}

/* ═══ FINAL CTA ═══ */
function FinalEd({ s, set, save }) {
  return (
    <div className="space-y-4">
      <Field label="Heading" value={s.final_heading || ""} onChange={v => set({ ...s, final_heading: v })} />
      <Field label="Copy" value={s.final_copy || ""} onChange={v => set({ ...s, final_copy: v })} textarea rows={4} />
      <Field label="Button Text" value={s.final_btn || ""} onChange={v => set({ ...s, final_btn: v })} />
      <Uploader label="Background Image" url={s.final_image} onUpload={url => { set({ ...s, final_image: url }); save({ final_image: url }); }} />
      <SaveBtn onClick={() => save({ final_heading: s.final_heading, final_copy: s.final_copy, final_btn: s.final_btn })} />
    </div>
  );
}

/* ═══ MEDIA KIT ═══ */
function MediaKitEd({ s, set, save }) {
  return (
    <div className="space-y-4">
      <div className="bg-[#111] border border-white/10 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase">Section Enabled</h4>
            <p className="text-[10px] text-[#8a857d] mt-0.5">Show or hide the Media Kit section.</p>
          </div>
          <Toggle checked={s.mk_enabled !== false} onChange={v => { set({ ...s, mk_enabled: v }); save({ mk_enabled: v }); }} />
        </div>
      </div>
      <Field label="Headline" value={s.mk_headline || ""} onChange={v => set({ ...s, mk_headline: v })} />
      <Field label="Subheadline" value={s.mk_subheadline || ""} onChange={v => set({ ...s, mk_subheadline: v })} textarea rows={2} />
      <Field label="Body Copy" value={s.mk_body || ""} onChange={v => set({ ...s, mk_body: v })} textarea rows={5} />
      <SaveBtn onClick={() => save({ mk_headline: s.mk_headline, mk_subheadline: s.mk_subheadline, mk_body: s.mk_body })} />

      <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase pt-2">Feature Cards</h4>
      <ArrayEditor items={s.mk_features || []} onSave={v => save({ mk_features: v })} renderItem={(it, i, upd) => (
        <div className="space-y-2 w-full">
          <input placeholder="Title" value={it.title} onChange={e => upd({ ...it, title: e.target.value })} className="stc-input w-full text-sm font-bold" />
          <input placeholder="Description" value={it.desc} onChange={e => upd({ ...it, desc: e.target.value })} className="stc-input w-full text-sm" />
        </div>
      )} newItem={() => ({ title: "", desc: "" })} />

      <h4 className="font-ui text-[11px] tracking-[0.2em] text-[#C9A055] uppercase pt-2">Buttons & PDF</h4>
      <Field label="Download Button Text" value={s.mk_btn_text || ""} onChange={v => set({ ...s, mk_btn_text: v })} />
      <Uploader label="Media Kit PDF" url={s.mk_pdf_url} onUpload={url => { set({ ...s, mk_pdf_url: url }); save({ mk_pdf_url: url }); }} accept=".pdf,application/pdf" />
      <Field label="Secondary Button Text" value={s.mk_btn2_text || ""} onChange={v => set({ ...s, mk_btn2_text: v })} />
      <Field label="Secondary Button Link" value={s.mk_btn2_link || ""} onChange={v => set({ ...s, mk_btn2_link: v })} />
      <Field label="Footnote" value={s.mk_footnote || ""} onChange={v => set({ ...s, mk_footnote: v })} />
      <Uploader label="Background Image (optional)" url={s.mk_bg_image} onUpload={url => { set({ ...s, mk_bg_image: url }); save({ mk_bg_image: url }); }} />
      <SaveBtn onClick={() => save({ mk_btn_text: s.mk_btn_text, mk_btn2_text: s.mk_btn2_text, mk_btn2_link: s.mk_btn2_link, mk_footnote: s.mk_footnote })} />
    </div>
  );
}

/* ═══ FOOTER ═══ */
function FooterEd({ s, set, save }) {
  return (
    <div className="space-y-4">
      <Field label="Contact Email (Leads)" value={s.contact_email || ""} onChange={v => set({ ...s, contact_email: v })} onBlur={() => save({ contact_email: s.contact_email })} />
      <Field label="Footer Disclaimer" value={s.footer_disclaimer || ""} onChange={v => set({ ...s, footer_disclaimer: v })} textarea rows={3} />
      <SaveBtn onClick={() => save({ footer_disclaimer: s.footer_disclaimer })} />
    </div>
  );
}

/* ═══ SHARED ═══ */
function Toggle({ checked, onChange }) {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="sr-only peer" />
      <div className="w-10 h-5 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:bg-[#D4AF37] transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5" />
    </label>
  );
}

function Field({ label, value, onChange, onBlur, textarea, rows = 3 }) {
  const Tag = textarea ? "textarea" : "input";
  return (
    <div>
      <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">{label}</label>
      <Tag value={value} onChange={e => onChange(e.target.value)} onBlur={onBlur} className="stc-input w-full" {...(textarea ? { rows } : {})} />
    </div>
  );
}

function SaveBtn({ onClick, label = "Save" }) {
  return (
    <div className="flex justify-end">
      <button onClick={onClick} className="btn-gold text-[10px]"><Save size={12} /> {label}</button>
    </div>
  );
}

function ArrayEditor({ items, onSave, renderItem, newItem }) {
  const [list, setList] = useState(items || []);
  useEffect(() => setList(items || []), [items]);
  const add = () => setList([...list, newItem()]);
  const remove = (idx) => setList(list.filter((_, i) => i !== idx));
  const move = (idx, dir) => { if (idx + dir < 0 || idx + dir >= list.length) return; const l = [...list]; [l[idx], l[idx + dir]] = [l[idx + dir], l[idx]]; setList(l); };
  const update = (idx, val) => { const l = [...list]; l[idx] = val; setList(l); };
  return (
    <div>
      <div className="flex justify-end mb-2">
        <button onClick={() => onSave(list)} className="btn-outline text-[10px]"><Save size={12} /> Save List</button>
      </div>
      <div className="space-y-2">
        {list.map((it, idx) => (
          <div key={idx} className="flex gap-2 items-start border border-white/5 bg-[#111] p-3">
            <div className="flex-1">{renderItem(it, idx, v => update(idx, v))}</div>
            <div className="flex flex-col gap-1 shrink-0">
              <button onClick={() => move(idx, -1)} className="btn-outline px-1.5"><MoveUp size={12} /></button>
              <button onClick={() => move(idx, 1)} className="btn-outline px-1.5"><MoveDown size={12} /></button>
              <button onClick={() => remove(idx)} className="btn-outline px-1.5 border-red-900/50 text-red-500"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
      <button onClick={add} className="btn-outline w-full mt-2 justify-center text-xs"><Plus size={14} /> Add</button>
    </div>
  );
}

function Uploader({ label, url, onUpload, accept = "image/*" }) {
  const isImage = accept.startsWith("image") || accept === "image/*";
  return (
    <div>
      <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1 uppercase">{label}</div>
      <div className="flex items-center gap-3 flex-wrap">
        {url ? (
          isImage ? <img src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-16 h-16 object-cover border border-white/10 rounded" />
            : <span className="text-xs text-[#C9A055] border border-[#C9A055]/20 px-3 py-2 rounded">File uploaded</span>
        ) : <div className="w-16 h-16 border border-white/10 bg-black flex items-center justify-center text-[#5a5650] text-[10px] rounded">none</div>}
        <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.22em] uppercase cursor-pointer">
          <input type="file" accept={accept} className="hidden" onChange={e => {
            const file = e.target.files?.[0]; if (!file) return;
            const reader = new FileReader();
            reader.onload = async () => {
              try {
                const { data } = await http.post("/upload/image", { data_uri: reader.result, filename: file.name });
                onUpload(data.url); toast.success("Uploaded");
              } catch (er) { toast.error(er?.response?.data?.detail || "Upload failed"); }
            };
            reader.readAsDataURL(file);
          }} />
          Upload
        </label>
      </div>
    </div>
  );
}
